import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { Plus, Trash2, Fuel, Loader2, ChevronDown, Check } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { FunctionsHttpError } from "@supabase/supabase-js";
import { calcFuelPence, roundTripMiles } from "@/lib/fuel";
import { listPastClients, type PastClient } from "@/lib/quickActions";
import {
  BookingFormInput,
  BookingWithClient,
  type ExtraDate,
  findOverlaps,
} from "@/lib/bookingsApi";
import {
  CATEGORY_LABELS,
  BOOKING_STATUS_LABELS,
  PAYMENT_METHOD_LABELS,
  PAYMENT_STATUS_LABELS,
  defaultDueDate,
  formatDisplayDate,
  DURATION_OPTIONS,
  durationLabel,
  addMinutes,
} from "@/lib/bookingLabels";
import type { BookingCategory, BookingStatus, PaymentMethod, PaymentStatus } from "@/types/database";

const field =
  "admin-field w-full rounded-xl bg-black/40 border border-white/10 px-4 py-3 text-white placeholder:text-white/30 focus:outline-none focus:border-primary font-inter text-sm";
const labelCls = "block text-xs uppercase tracking-widest text-white/50 mb-2";

type Props = {
  initial: BookingFormInput;
  mode: "create" | "edit";
  saving?: boolean;
  onSave: (values: BookingFormInput, extras: ExtraDate[]) => Promise<void>;
  onSaveAndSend?: (values: BookingFormInput, extras: ExtraDate[]) => Promise<void>;
  excludeBookingId?: string;
};

const DurationSelect = ({ id, value, onChange }: { id?: string; value: string; onChange: (v: string) => void }) => {
  const known = DURATION_OPTIONS.map(String);
  return (
    <select id={id} className={field} value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">Choose</option>
      {value && !known.includes(value) && <option value={value}>{durationLabel(Number(value))}</option>}
      {DURATION_OPTIONS.map((m) => (
        <option key={m} value={m}>{durationLabel(m)}</option>
      ))}
    </select>
  );
};

const TbcToggle = ({ on, onClick }: { on: boolean; onClick: () => void }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={on}
    className={`rounded-full px-3 py-1.5 text-xs font-inter border transition-colors ${
      on ? "bg-primary text-primary-foreground border-primary" : "border-white/15 text-white/60 hover:border-primary/50"
    }`}
  >
    Date TBC
  </button>
);

const BookingForm = ({
  initial,
  mode,
  saving,
  onSave,
  onSaveAndSend,
  excludeBookingId,
}: Props) => {
  const [values, setValues] = useState<BookingFormInput>(initial);
  const [error, setError] = useState<string | null>(null);
  const [overlaps, setOverlaps] = useState<BookingWithClient[]>([]);
  const [sendNote, setSendNote] = useState<string | null>(null);
  const [extras, setExtras] = useState<ExtraDate[]>([]);
  const [fuelCfg, setFuelCfg] = useState({ included: 10, ratePence: 100 });
  const [fuelBusy, setFuelBusy] = useState(false);
  const [fuelMsg, setFuelMsg] = useState<string | null>(null);

  useEffect(() => {
    void supabase
      ?.from("app_settings")
      .select("fuel_included_miles, fuel_rate_pence")
      .eq("id", 1)
      .maybeSingle()
      .then(({ data }) => {
        const d = data as { fuel_included_miles?: number; fuel_rate_pence?: number } | null;
        if (d) setFuelCfg({ included: d.fuel_included_miles ?? 10, ratePence: d.fuel_rate_pence ?? 100 });
      });
  }, []);

  const workOutFuel = async (postcode?: string) => {
    const dest = (postcode ?? values.venue_postcode).trim();
    if (!dest || !supabase) {
      setFuelMsg(null);
      return;
    }
    setFuelBusy(true);
    setFuelMsg(null);
    const { data, error } = await supabase.functions.invoke("fuel-distance", { body: { destination: dest } });
    setFuelBusy(false);
    if (error) {
      let msg = "Could not work out the distance.";
      if (error instanceof FunctionsHttpError) {
        try { msg = (await error.context.json())?.error || msg; } catch { /* keep default */ }
      }
      setFuelMsg(msg);
      return;
    }
    const reply = data as { miles?: number; error?: string } | null;
    if (reply?.error) {
      setFuelMsg(reply.error);
      return;
    }
    const oneWay = Number(reply?.miles ?? 0);
    const miles = roundTripMiles(oneWay);
    const pence = calcFuelPence(miles, fuelCfg.included, fuelCfg.ratePence);
    const charge = fuelOnRef.current ? pence : 0;
    setValues((v) => ({ ...v, distance_miles: String(miles), fuel_pounds: (charge / 100).toFixed(2) }));
    setFuelMsg(
      pence > 0
        ? `${oneWay} miles each way, ${miles} there and back. ${fuelCfg.included} free, so ${(miles - fuelCfg.included).toFixed(1)} miles charged.`
        : `${oneWay} miles each way, ${miles} there and back. Within your ${fuelCfg.included} free miles, so no fuel charge.`
    );
  };

  // Fuel on/off: off means no fuel charge at all for this booking.
  const [fuelOn, setFuelOn] = useState(() => {
    const miles = Number(initial.distance_miles);
    const fuel = Number.parseFloat(initial.fuel_pounds) || 0;
    return !(mode === "edit" && fuel === 0 && Number.isFinite(miles) && miles > 10);
  });
  const fuelOnRef = useRef(fuelOn);
  fuelOnRef.current = fuelOn;
  const toggleFuel = () => {
    const next = !fuelOn;
    setFuelOn(next);
    fuelOnRef.current = next;
    if (!next) {
      setValues((v) => ({ ...v, fuel_pounds: "0.00" }));
      return;
    }
    const miles = Number(values.distance_miles);
    if (values.distance_miles && Number.isFinite(miles)) {
      const pence = calcFuelPence(miles, fuelCfg.included, fuelCfg.ratePence);
      setValues((v) => ({ ...v, fuel_pounds: (pence / 100).toFixed(2) }));
    } else if (values.venue_postcode.trim()) {
      void workOutFuel();
    }
  };

  const [pastClients, setPastClients] = useState<PastClient[]>([]);
  const [suggestOpen, setSuggestOpen] = useState(false);
  useEffect(() => {
    if (mode === "create") void listPastClients().then(setPastClients);
  }, [mode]);
  const suggestions = useMemo(() => {
    const q = values.venue_or_client_name.trim().toLowerCase();
    if (!q || values.client_id) return [];
    return pastClients.filter((c) => c.venue_or_client_name.toLowerCase().includes(q) || c.contact_name.toLowerCase().includes(q)).slice(0, 5);
  }, [pastClients, values.venue_or_client_name, values.client_id]);
  const pickClient = (c: PastClient) => {
    setValues((v) => ({
      ...v,
      client_id: c.client_id,
      venue_or_client_name: c.venue_or_client_name,
      contact_name: c.contact_name,
      email: c.email,
      phone: c.phone,
      venue_postcode: c.venue_postcode,
      address: c.address,
      category: c.category as BookingCategory,
      fee_pounds: c.fee_pounds,
    }));
    setSuggestOpen(false);
  };

  const [showMore, setShowMore] = useState(mode === "edit");
  const lastPc = useRef(initial.venue_postcode?.trim().toUpperCase() ?? "");
  useEffect(() => {
    const pc = values.venue_postcode.trim().toUpperCase();
    const looksFull = /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/.test(pc);
    if (!looksFull || pc === lastPc.current) return;
    const t = setTimeout(() => {
      lastPc.current = pc;
      void workOutFuel(pc);
    }, 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.venue_postcode, fuelCfg]);

  const addExtra = () =>
    setExtras((xs) => [
      ...xs,
      { performance_date: "", date_tbc: false, start_time: "", end_time: "", duration_minutes: values.duration_minutes, address: values.address, fee_pounds: values.fee_pounds },
    ]);
  const setExtra = (i: number, key: keyof ExtraDate) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setExtras((xs) => xs.map((x, j) => (j === i ? { ...x, [key]: e.target.value } : x)));
  const toggleExtraTbc = (i: number) =>
    setExtras((xs) => xs.map((x, j) => (j === i ? { ...x, date_tbc: !x.date_tbc, performance_date: "", start_time: "", end_time: "" } : x)));
  const extrasTotal = extras.reduce((s, x) => s + (Number.parseFloat(x.fee_pounds) || 0), 0) + (Number.parseFloat(values.fee_pounds) || 0);

  useEffect(() => {
    setValues(initial);
  }, [initial]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!values.performance_date || values.date_tbc) {
        setOverlaps([]);
        return;
      }
      const res = await findOverlaps({
        performance_date: values.performance_date,
        start_time: values.start_time || null,
        end_time: values.start_time && values.duration_minutes ? addMinutes(values.start_time, Number(values.duration_minutes)) : values.end_time || null,
        excludeId: excludeBookingId,
      });
      if (cancelled) return;
      if (res.ok) setOverlaps(res.data);
      else setOverlaps([]);
    };
    const t = setTimeout(run, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [
    values.performance_date,
    values.start_time,
    values.end_time,
    values.duration_minutes,
    excludeBookingId,
  ]);

  const set =
    (key: keyof BookingFormInput) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      const value = e.target.value;
      setValues((prev) => {
        const next = { ...prev, [key]: value };
        if (key === "performance_date" && value) {
          // Keep due date at default offset when creating / when due empty
          if (mode === "create" || !prev.due_date) {
            next.due_date = defaultDueDate(value, 7);
          }
        }
        if ((key === "start_time" || key === "end_time") && next.start_time && next.end_time) {
          const [sh, sm] = next.start_time.split(":").map(Number);
          const [eh, em] = next.end_time.split(":").map(Number);
          const mins = eh * 60 + em - (sh * 60 + sm);
          if (mins > 0) next.duration_minutes = String(mins);
        }
        return next;
      });
    };

  const submit = async (e: FormEvent, andSend = false) => {
    e.preventDefault();
    setError(null);
    setSendNote(null);
    try {
      if (andSend && onSaveAndSend) {
        await onSaveAndSend(values, extras);
        setSendNote("Saving and opening payment-request preview…");
      } else {
        await onSave(values, extras);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save booking.");
    }
  };

  const overlapText = useMemo(
    () =>
      overlaps.map((o) => `${o.reference} · ${o.clients?.venue_or_client_name ?? "—"}`).join("; "),
    [overlaps]
  );

  const totalOne = (Number.parseFloat(values.fee_pounds) || 0) + (Number.parseFloat(values.fuel_pounds) || 0);
  const fuelOne = Number.parseFloat(values.fuel_pounds) || 0;
  const grandTotal = extrasTotal + fuelOne * (extras.length + 1);
  const step = (n: number, title: string) => (
    <div className="flex items-center gap-3 mb-1">
      <span className="w-7 h-7 rounded-full bg-primary/15 text-primary text-xs font-inter flex items-center justify-center shrink-0">{n}</span>
      <h2 className="font-playfair text-xl">{title}</h2>
    </div>
  );

  return (
    <form className="space-y-5 pb-28 lg:pb-24" onSubmit={(e) => submit(e, false)}>
      <section className="admin-card p-5 md:p-6 space-y-4">
        {step(1, "Client details")}
        <div>
          <label className={labelCls} htmlFor="venue">Venue or client name *</label>
          <div className="relative">
            <input
              id="venue"
              required
              autoComplete="off"
              className={field}
              value={values.venue_or_client_name}
              onChange={(e) => {
                const v = e.target.value;
                setValues((x) => ({ ...x, venue_or_client_name: v, client_id: mode === "create" ? undefined : x.client_id }));
                setSuggestOpen(true);
              }}
              onBlur={() => setTimeout(() => setSuggestOpen(false), 150)}
              placeholder="Start typing to find a past client"
            />
            {suggestOpen && suggestions.length > 0 && (
              <ul className="absolute z-20 left-0 right-0 mt-2 rounded-xl border border-white/10 bg-secondary shadow-xl overflow-hidden">
                {suggestions.map((c) => (
                  <li key={c.client_id}>
                    <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => pickClient(c)} className="w-full text-left px-4 py-3 hover:bg-primary/10 font-inter">
                      <span className="block text-sm text-white">{c.venue_or_client_name}</span>
                      <span className="block text-xs text-white/45">{[c.contact_name, c.venue_postcode, `£${c.fee_pounds}`].filter(Boolean).join(" · ")}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          {values.client_id && mode === "create" && (
            <p className="text-xs text-primary mt-2 font-inter">Past client. Details filled in from their last booking.</p>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className={labelCls} htmlFor="contact">Contact name</label>
            <input id="contact" className={field} value={values.contact_name} onChange={set("contact_name")} />
          </div>
          <div>
            <label className={labelCls} htmlFor="email">Email</label>
            <input id="email" type="email" inputMode="email" className={field} value={values.email} onChange={set("email")} />
          </div>
          <div>
            <label className={labelCls} htmlFor="extraEmail">Extra email (optional)</label>
            <input id="extraEmail" type="email" inputMode="email" className={field} value={values.extra_email ?? ""} onChange={set("extra_email")} placeholder="Also gets the payment link and reminders" />
          </div>
          <div>
            <label className={labelCls} htmlFor="phone">Phone</label>
            <input id="phone" type="tel" inputMode="tel" className={field} value={values.phone} onChange={set("phone")} />
          </div>
        </div>
      </section>

      <section className="admin-card p-5 md:p-6 space-y-4">
        {step(2, "Performance")}
        <div className="flex items-center justify-between">
          <p className="text-xs uppercase tracking-widest text-primary/80 font-inter">{extras.length ? "Performance 1" : "Date and time"}</p>
          <TbcToggle on={!!values.date_tbc} onClick={() => setValues((v) => ({ ...v, date_tbc: !v.date_tbc }))} />
        </div>
        {values.date_tbc ? (
          <p className="rounded-xl border border-dashed border-white/15 px-4 py-3 text-sm text-white/55 font-inter">Date to be confirmed. They can still pay for it now.</p>
        ) : (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="col-span-2">
            <label className={labelCls} htmlFor="date">Date *</label>
            <input id="date" type="date" required className={field} value={values.performance_date} onChange={set("performance_date")} />
          </div>
          <div>
            <label className={labelCls} htmlFor="start">Start</label>
            <input id="start" type="time" className={field} value={values.start_time} onChange={set("start_time")} />
          </div>
          <div>
            <label className={labelCls} htmlFor="dur">How long</label>
            <DurationSelect id="dur" value={values.duration_minutes} onChange={(v) => setValues((x) => ({ ...x, duration_minutes: v }))} />
          </div>
        </div>
        )}
        {!values.date_tbc && values.start_time && values.duration_minutes && (
          <p className="text-xs text-white/50 font-inter -mt-2">{values.start_time} to {addMinutes(values.start_time, Number(values.duration_minutes))} ({durationLabel(Number(values.duration_minutes))})</p>
        )}
        <div className="sm:w-1/2">
          <label className={labelCls} htmlFor="fee">Performance fee £ *</label>
          <input id="fee" type="number" inputMode="decimal" min={0} step="0.01" required className={field} value={values.fee_pounds} onChange={set("fee_pounds")} />
        </div>
        <div>
          <label className={labelCls} htmlFor="category">Type of booking</label>
          <div className="flex flex-wrap gap-2" id="category">
            {(Object.keys(CATEGORY_LABELS) as BookingCategory[]).map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => setValues((v) => ({ ...v, category: k }))}
                className={`rounded-full px-4 py-2 text-sm font-inter border transition-colors ${
                  values.category === k ? "bg-primary text-primary-foreground border-primary" : "border-white/15 text-white/70 hover:border-primary/50"
                }`}
              >
                {CATEGORY_LABELS[k]}
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_2fr] gap-4">
          <div>
            <div className="flex items-center justify-between gap-2 mb-1.5">
              <label className={labelCls + " !mb-0"} htmlFor="vpc">Venue postcode</label>
              <button
                type="button"
                role="switch"
                aria-checked={fuelOn}
                aria-label="Charge fuel"
                onClick={toggleFuel}
                className="inline-flex items-center gap-2 text-xs font-inter text-white/70"
              >
                <Fuel className={`w-3.5 h-3.5 ${fuelOn ? "text-primary" : "text-white/35"}`} />
                <span>{fuelOn ? "Fuel on" : "No fuel"}</span>
                <span className={`relative inline-flex h-6 w-11 shrink-0 rounded-full border transition-colors ${fuelOn ? "bg-primary border-primary" : "bg-white/10 border-white/20"}`}>
                  <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-background shadow transition-transform ${fuelOn ? "translate-x-[22px]" : "translate-x-0.5"}`} />
                </span>
              </button>
            </div>
            <div className="relative">
              <input id="vpc" className={field + " uppercase pr-10"} value={values.venue_postcode} onChange={set("venue_postcode")} placeholder="e.g. WS8 6NZ" autoComplete="postal-code" />
              {fuelBusy && <Loader2 className="w-4 h-4 animate-spin text-primary absolute right-3 top-1/2 -translate-y-1/2" />}
            </div>
          </div>
          <div>
            <label className={labelCls} htmlFor="address">Address (optional)</label>
            <input id="address" className={field} value={values.address} onChange={set("address")} placeholder="Street and town" />
          </div>
        </div>
        {!fuelOn ? (
          <p className="text-xs text-white/50 font-inter">Fuel is off for this booking, so no fuel is charged.</p>
        ) : fuelMsg ? (
          <div className="flex items-start gap-2 rounded-xl bg-primary/5 border border-primary/20 px-4 py-3 text-sm text-white/75 font-inter">
            <Fuel className="w-4 h-4 text-primary shrink-0 mt-0.5" />
            <span className="flex-1">{fuelMsg}</span>
            <button type="button" onClick={() => void workOutFuel()} className="text-xs text-primary underline shrink-0">Recheck</button>
          </div>
        ) : (
          <p className="text-xs text-white/40 font-inter">Type the postcode and the fuel charge is worked out for you, there and back.</p>
        )}
        {mode === "create" && (
          <div className="space-y-3 pt-2 border-t border-white/5">
            {extras.map((x, i) => (
              <div key={i} className="rounded-xl border border-white/10 bg-black/30 p-3 space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xs uppercase tracking-widest text-primary/80 font-inter">Performance {i + 2}</p>
                  <div className="flex items-center gap-2">
                    <TbcToggle on={!!x.date_tbc} onClick={() => toggleExtraTbc(i)} />
                    <button type="button" aria-label="Remove performance" onClick={() => setExtras((xs) => xs.filter((_, j) => j !== i))} className="h-10 w-10 inline-flex items-center justify-center rounded-full border border-white/10 text-white/50 hover:text-destructive">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-[1.4fr_1fr_1fr_1fr] gap-3">
                  {x.date_tbc ? (
                    <p className="col-span-2 sm:col-span-3 rounded-xl border border-dashed border-white/15 px-4 py-3 text-sm text-white/55 font-inter self-end">Date TBC</p>
                  ) : (
                    <>
                      <div className="col-span-2 sm:col-span-1">
                        <label className={labelCls}>Date *</label>
                        <input type="date" required className={field} value={x.performance_date} onChange={setExtra(i, "performance_date")} />
                      </div>
                      <div>
                        <label className={labelCls}>Start</label>
                        <input type="time" className={field} value={x.start_time} onChange={setExtra(i, "start_time")} />
                      </div>
                      <div>
                        <label className={labelCls}>How long</label>
                        <DurationSelect value={x.duration_minutes ?? ""} onChange={(v) => setExtras((xs) => xs.map((y, j) => (j === i ? { ...y, duration_minutes: v } : y)))} />
                      </div>
                    </>
                  )}
                  <div className="col-span-2 sm:col-span-1">
                    <label className={labelCls}>Fee £</label>
                    <input type="number" inputMode="decimal" min={0} step="0.01" className={field} value={x.fee_pounds} onChange={setExtra(i, "fee_pounds")} />
                  </div>
                </div>
                {!x.date_tbc && x.start_time && x.duration_minutes && (
                  <p className="text-xs text-white/50 font-inter">{x.start_time} to {addMinutes(x.start_time, Number(x.duration_minutes))} ({durationLabel(Number(x.duration_minutes))})</p>
                )}
              </div>
            ))}
            <button type="button" onClick={addExtra} className="w-full inline-flex items-center justify-center gap-2 rounded-full border border-dashed border-primary/50 text-primary px-4 py-3 text-sm hover:bg-primary/10">
              <Plus className="w-4 h-4" /> Add another performance
            </button>
            {extras.length > 0 && (
              <p className="text-xs text-white/45 font-inter">Same client, venue and fuel. They can pay together or one at a time.</p>
            )}
          </div>
        )}
        {overlaps.length > 0 && (
          <div className="rounded-xl border border-primary/40 bg-primary/10 px-4 py-3 text-sm text-white/85 font-inter" role="status">
            <strong className="text-primary">Heads up:</strong> you already have a booking on {formatDisplayDate(values.performance_date)}
            {values.start_time ? ` around ${values.start_time}` : ""}. {overlapText}. You can still save.
          </div>
        )}
      </section>

      <section className="admin-card p-5 md:p-6 space-y-4">
        {step(3, extras.length ? "Totals for all performances" : "Total and pay by")}
        <ul className="divide-y divide-white/5 rounded-xl border border-white/10 bg-black/30 font-inter text-sm">
          {[{ label: values.date_tbc ? "Date TBC" : values.performance_date ? formatDisplayDate(values.performance_date) : "Performance 1", fee: values.fee_pounds }, ...extras.map((x, i) => ({ label: x.date_tbc ? "Date TBC" : x.performance_date ? formatDisplayDate(x.performance_date) : `Performance ${i + 2}`, fee: x.fee_pounds }))].map((r, i) => (
            <li key={i} className="flex justify-between gap-3 px-4 py-2.5">
              <span className="text-white/70">{r.label}</span>
              <span className="text-white/90">£{(Number.parseFloat(r.fee) || 0).toFixed(2)} <span className="text-white/40">+ £{fuelOne.toFixed(2)} fuel</span></span>
            </li>
          ))}
        </ul>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelCls} htmlFor="fuel">Fuel £ {extras.length ? "(each)" : ""}</label>
            <input id="fuel" type="number" inputMode="decimal" min={0} step="0.01" className={field} value={values.fuel_pounds} onChange={set("fuel_pounds")} />
          </div>
          <div>
            <label className={labelCls} htmlFor="due">Pay by</label>
            <input id="due" type="date" className={field} value={values.due_date} onChange={set("due_date")} />
          </div>
        </div>
        <div className="rounded-xl border border-primary/40 bg-primary/10 px-4 py-3 font-inter flex items-end justify-between">
          <p className="text-[11px] uppercase tracking-widest text-white/50">{extras.length ? `Total for ${extras.length + 1} performances` : "Total"}</p>
          <p className="text-primary text-2xl leading-tight">£{(extras.length ? grandTotal : totalOne).toFixed(2)}</p>
        </div>
        <p className="text-xs text-white/40 font-inter">Fuel is worked out for you but you can change it.</p>
      </section>

      <section className="admin-card p-0 overflow-hidden">
        <button type="button" onClick={() => setShowMore((v) => !v)} className="w-full flex items-center justify-between px-5 md:px-6 py-4 text-left">
          <span className="font-inter text-sm text-white/70">More options <span className="text-white/35">(notes, payment method, status)</span></span>
          <ChevronDown className={`w-4 h-4 text-white/50 transition-transform ${showMore ? "rotate-180" : ""}`} />
        </button>
        {showMore && (
          <div className="px-5 md:px-6 pb-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className={labelCls} htmlFor="clientDesc">Note for the client</label>
              <textarea id="clientDesc" rows={2} className={field} value={values.client_description} onChange={set("client_description")} placeholder="Shown on their payment page" />
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls} htmlFor="notes">Private notes</label>
              <textarea id="notes" rows={2} className={field} value={values.internal_notes} onChange={set("internal_notes")} placeholder="Only you see these" />
            </div>
            <div>
              <label className={labelCls} htmlFor="method">Payment method</label>
              <select id="method" className={field} value={values.preferred_payment_method} onChange={set("preferred_payment_method")}>
                {(Object.keys(PAYMENT_METHOD_LABELS) as PaymentMethod[]).map((k) => (<option key={k} value={k}>{PAYMENT_METHOD_LABELS[k]}</option>))}
              </select>
            </div>
            <div>
              <label className={labelCls} htmlFor="payStatus">Payment status</label>
              <select id="payStatus" className={field} value={values.payment_status} onChange={set("payment_status")}>
                {(Object.keys(PAYMENT_STATUS_LABELS) as PaymentStatus[]).map((k) => (<option key={k} value={k}>{PAYMENT_STATUS_LABELS[k]}</option>))}
              </select>
            </div>
            {mode === "edit" && (
              <div>
                <label className={labelCls} htmlFor="bookStatus">Booking status</label>
                <select id="bookStatus" className={field} value={values.booking_status} onChange={set("booking_status")}>
                  {(Object.keys(BOOKING_STATUS_LABELS) as BookingStatus[]).map((k) => (<option key={k} value={k}>{BOOKING_STATUS_LABELS[k]}</option>))}
                </select>
              </div>
            )}
          </div>
        )}
      </section>

      {error && (
        <div className="rounded-xl border border-destructive/50 bg-destructive/15 px-4 py-3 text-sm font-inter" role="alert">
          <p className="font-medium">Could not save booking</p>
          <p className="leading-relaxed whitespace-pre-wrap text-white/80">{error}</p>
        </div>
      )}
      {sendNote && <p className="text-sm text-primary font-inter" role="status">{sendNote}</p>}

      <div className="fixed inset-x-0 bottom-[calc(57px+env(safe-area-inset-bottom))] lg:bottom-0 z-30 border-t border-white/10 bg-black/90 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3">
          <div className="hidden sm:block font-inter mr-auto">
            <p className="text-[11px] uppercase tracking-widest text-white/45">{extras.length > 0 ? `${extras.length + 1} dates` : "Total"}</p>
            <p className="text-primary text-lg leading-tight">£{(extras.length > 0 ? grandTotal : totalOne).toFixed(2)}</p>
          </div>
          <button type="submit" disabled={saving} className="flex-1 sm:flex-none rounded-full border border-white/20 px-6 py-3 text-sm hover:border-primary disabled:opacity-50">
            {saving ? "Saving…" : "Save"}
          </button>
          {onSaveAndSend && (
            <button type="button" disabled={saving} onClick={(e) => submit(e as unknown as FormEvent, true)} className="flex-[2] sm:flex-none inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground font-medium px-6 py-3 text-sm hover:bg-primary/90 disabled:opacity-50">
              <Check className="w-4 h-4" /> Save and send
            </button>
          )}
        </div>
      </div>
    </form>
  );
};

export default BookingForm;
